import { useRouter } from 'next/router';
import { GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import dynamic from 'next/dynamic';

/* Components */
import SkuPageContainer from '@scenes/ServiceSkuPage';
import { getLayout } from '@components/PageLayouts/Layout/Standard';

/* Apis */
import { skus, reviews } from '@api/apis';

/* Utils */
import { findCustomPartnerAndMerge } from '@integrations/partners/partners.utils';

/* Constants */
import { PAGE_TYPE_SERVICES } from '@constants/pages';
import { SEVEN_DAYS } from '@constants/CacheRevalidate';
import { LAYOUT_PAGE_TYPES } from '@components/PageLayouts/pagelayouts.constants';

/* Types */
import { SkuPageContainerType } from 'types/sku.types';
import { Children } from 'types/base.types';
import { RouteSkuLinks, RouteSkuParams } from 'types/routes.types';

/* Dynamic Components */
const SkeletonLoading = dynamic(() => import('@components/Loaders/Skeleton/Skeleton.sku'), { ssr: false });

/*
 * Our base Services Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */
function SkuPage(props: SkuPageContainerType): Children {
  const router = useRouter();

  if (router.isFallback) {
    return <SkeletonLoading />;
  }

  return <SkuPageContainer {...props} />;
}

SkuPage.pageAttributes = { page: PAGE_TYPE_SERVICES };
SkuPage.Layout = getLayout({ promo: true, footer: true });

export default SkuPage;

/**
 * ---------------------------------------------------------------------------------------
 * Static Generation
 * @param params
 * ---------------------------------------------------------------------------------------
 */
interface StaticParams extends ParsedUrlQuery {
  seoName: string;
}

/**
 * - Get sku data
 * - Get reviews for sku
 * - Get routes for navigation (predone for rank/column)
 *
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns {Promise<*>}
 */
export const getStaticProps: GetStaticProps = async context => {
  const params = context.params as StaticParams;
  const { data: { sku = '' } = {} } = await skus.find({ id: params.seoName });
  const { data: { reviews: reviewResponse = [] } = {} } = await reviews.sku({ id: sku?.id });
  if (!sku || sku.error) {
    throw new Error(`Service '${params!.seoName}' not found`);
  }

  /* Lets merge our passed partner with a potential custom/default partner */
  const partnerSkuData = findCustomPartnerAndMerge(sku.partner, { mergedAttributes: { page: LAYOUT_PAGE_TYPES.PRECART } });
  /* Lets merge our sku data since its SSR into our sku partner. So we can get the benefit of having all partner stuff upfront */
  if (partnerSkuData) {
    sku.partner = partnerSkuData;
  }

  return {
    props: {
      sku: { ...sku },
      reviews: [...reviewResponse],
      partner: partnerSkuData,
    },
    revalidate: SEVEN_DAYS,
  };
};

/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * - Get navigatable service skus
 * - Pluck out just the seoName for pathing for routes
 *
 * @returns {Promise<{paths: RouteSkuParams<string>[] | undefined; fallback: boolean}>}
 */
export async function getStaticPaths<GetStaticPaths>() {
  const { data } = await skus.all();
  const allSkus = data?.routes?.service_categories.reduce((skusRoute: RouteSkuParams<string>[], item: RouteSkuLinks) => {
    item.skus.forEach((sku: $FixMe) => {
      skusRoute.push({
        params: {
          seoName: String(sku.seo_name),
        },
      });
    });

    return skusRoute;
  }, []);

  return {
    paths: allSkus,
    fallback: true,
  };
}
